import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Pagination from "../../Teachers/MemberList/Pagination";
import axiosInstance from "../../../../axios";
import moment from "moment/moment";
import { useNavigate } from "react-router-dom";
import {
    Trophy, CheckCircle2, XCircle, Calendar, Hash,
    BarChart2, ArrowUpRight, Search, BookOpen, Target,
    TrendingUp, Award, ChevronRight, Inbox, PlayCircle, ArrowRight
} from "lucide-react";
import Modal from "../../../Other/Modal";

const QuizResult = () => {
    const { user } = useSelector((state) => state.users);
    const isInstructor = user?.role == "4";

    const [fetchRecord, setFetchRecord] = useState([]);
    const [totalQuizPage, setTotalQuizPage] = useState(0);
    const [totalQuizCount, setTotalQuizCount] = useState(0);
    const [pageNum, setPageNum] = useState(1);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [courses, setCourses] = useState([]);
    const navigate = useNavigate();

    // Review Answers Modal States
    const [selectedTestId, setSelectedTestId] = useState(null);
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [reviewData, setReviewData] = useState(null);
    const [loadingReview, setLoadingReview] = useState(false);
    const [reviewError, setReviewError] = useState(null);

    const handleOpenReview = async (testId) => {
        setSelectedTestId(testId);
        setIsReviewModalOpen(true);
        setLoadingReview(true);
        setReviewError(null);
        setReviewData(null);
        try {
            const res = await axiosInstance.get(`/test/detail/${testId}`);
            setReviewData(res?.data?.data || null);
        } catch (err) {
            console.error("Error fetching test detail:", err);
            setReviewError("Failed to load review details. Please try again.");
        } finally {
            setLoadingReview(false);
        }
    };

    const handleCloseReview = () => {
        setIsReviewModalOpen(false);
        setSelectedTestId(null);
        setReviewData(null);
        setReviewError(null);
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                // Fetch purchased courses (student) or instructor's own courses (instructor)
                const url = isInstructor ? "/my-courses?perPage=10000" : "/course/purchased";
                const courseRes = await axiosInstance.get(url);
                const fetchedCourses = courseRes?.data?.data?.data || [];
                setCourses(fetchedCourses);

                if (fetchedCourses.length > 0) {
                    if (selectedCourse) {
                        const res = await axiosInstance.get(`/test/list?page=${pageNum}&perPage=8&courseName=${encodeURIComponent(selectedCourse)}`);
                        setTotalQuizPage(res?.data?.data?.total_page || 0);
                        setTotalQuizCount(res?.data?.data?.total || 0);
                        setFetchRecord(res?.data?.data?.data || []);
                    } else {
                        // Fetch everything for the overview (use high limit to get all records for averages/progress)
                        const allRes = await axiosInstance.get(`/test/list?perPage=10000`);
                        setFetchRecord(allRes?.data?.data?.data || []);
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();
    }, [pageNum, selectedCourse, isInstructor]);

    // Construct course list from all purchased courses
    const courseList = courses.map(course => {
        const courseName = course.CourseName;
        const results = fetchRecord.filter(r => r.CourseName === courseName);
        const totalScore = results.reduce((acc, curr) => {
            const total = curr.rightAns + curr.wrongAns || 1;
            const pct = (curr.rightAns / total) * 100;
            return acc + pct;
        }, 0);
        const passedCount = results.filter(curr => {
            const total = curr.rightAns + curr.wrongAns || 1;
            const pct = (curr.rightAns / total) * 100;
            return pct >= 70;
        }).length;

        return {
            name: courseName,
            id: course.ID,
            progress: course.progress,
            results,
            avgScore: results.length > 0 ? (totalScore / results.length).toFixed(0) : "0",
            passedCount
        };
    });

    const filteredCourses = courseList.filter((c) =>
        !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredRecords = selectedCourse
        ? fetchRecord.filter(r => (r.CourseName || "General Quizzes") === selectedCourse)
        : [];

    const checkIsCourseStarted = (course) => {
        if (isInstructor) return true;
        if (!course) return false;
        if ((course.progress?.finishedSection || 0) > 0) return true;
        try {
            const saved = localStorage.getItem("started_courses");
            if (saved) {
                const startedSet = new Set(JSON.parse(saved).map(String));
                return startedSet.has(String(course.ID || course.id)) || startedSet.has(String(course.CourseName));
            }
        } catch (e) {
            console.error(e);
        }
        return false;
    };

    const selectedCourseObj = courses.find(c => c.CourseName === selectedCourse);
    const isSelectedCourseStarted = checkIsCourseStarted(selectedCourseObj);

    const getScoreColor = (pct) => {
        if (pct >= 70) return { text: "text-emerald-600", bg: "bg-emerald-500", badge: "bg-emerald-50 text-emerald-700 border-emerald-100" };
        if (pct >= 50) return { text: "text-amber-600", bg: "bg-amber-500", badge: "bg-amber-50 text-amber-700 border-amber-100" };
        return { text: "text-rose-600", bg: "bg-rose-500", badge: "bg-rose-50 text-rose-700 border-rose-100" };
    };

    return (
        <div className="min-h-screen bg-[#f0f4f8] py-10 px-4 md:px-8">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-5">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            {selectedCourse && (
                                <button
                                    onClick={() => setSelectedCourse(null)}
                                    className="p-1.5 bg-white border border-slate-200 rounded-lg text-slate-400 hover:text-[#264b82] transition-all"
                                >
                                    <ChevronRight className="rotate-180 w-4 h-4" />
                                </button>
                            )}
                            <h2 className="text-3xl font-black text-slate-800 tracking-tight">
                                {selectedCourse 
                                    ? (isInstructor ? "Student Course Performance" : "Course Performance") 
                                    : (isInstructor ? "Student Performance Analytics" : "Performance Analytics")}
                            </h2>
                        </div>
                        <p className="text-slate-500 text-sm font-medium">
                            {selectedCourse ? `Viewing results for ${selectedCourse}`
                                : courses.length === 0 
                                    ? (isInstructor ? "You must create a course to view student performance analytics" : "You must purchase a course to view performance analytics")
                                    : (isInstructor ? "Track student quiz history and progress per course" : "Track your quiz history and progress per course")}
                        </p>
                    </div>
                    <div className="relative w-full md:w-72">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder={selectedCourse ? "Search quizzes..." : "Search courses..."}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#264b82]/20 focus:border-[#264b82] transition-all shadow-sm"
                        />
                    </div>
                </div>

                {loading ? (
                    <ResultsSkeleton isCourseList={!selectedCourse} />
                ) : courses.length === 0 ? (
                    <div className="flex flex-col items-center justify-center min-h-[60vh] bg-white rounded-[2rem] border border-slate-200 p-8 md:p-16 text-center max-w-2xl mx-auto shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="bg-blue-50 w-24 h-24 rounded-3xl flex items-center justify-center mb-6 shadow-sm border border-blue-100">
                            <BookOpen size={48} className="text-[#264b82]" />
                        </div>
                        <h3 className="text-2xl font-black text-slate-800 tracking-tight mb-3">
                            {isInstructor ? "You have not created any courses" : "you are not purchased any courses"}
                        </h3>
                        <p className="text-slate-500 text-base font-semibold max-w-md mb-8 leading-relaxed">
                            {isInstructor 
                                ? "It looks like you haven't created any courses yet. Create a course and add quizzes to track student performance."
                                : "It looks like you haven't enrolled in any courses yet. Expand your skills and unlock assessments by exploring our course catalog."}
                        </p>
                        <button
                            onClick={() => navigate(isInstructor ? "/dashboard/coursebuilder" : "/courses")}
                            className="group flex items-center gap-2 bg-[#264b82] text-white hover:bg-[#1e3a6e] px-8 py-4 rounded-2xl font-black text-base uppercase tracking-wider transition-all shadow-lg shadow-blue-900/15 active:scale-95 border border-[#264b82]"
                        >
                            {isInstructor ? "create course" : "explore course"} <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                    </div>
                ) : (
                    <>
                        {!selectedCourse ? (
                            /* Course Grid View */
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {filteredCourses.length > 0 ? (
                                    filteredCourses.map((course, idx) => {
                                        const isCourseStarted = checkIsCourseStarted(course);
                                        return (
                                            <div
                                                key={idx}
                                                onClick={() => {
                                                    setSelectedCourse(course.name);
                                                    setSearchQuery("");
                                                    setPageNum(1);
                                                }}
                                                className="bg-white rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all p-6 cursor-pointer group"
                                            >
                                                <div className="flex justify-between items-start mb-4">
                                                    <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-[#264b82] group-hover:bg-[#264b82] group-hover:text-white transition-all">
                                                        <BookOpen size={24} />
                                                    </div>
                                                    {isCourseStarted ? (
                                                        <div className="flex flex-col items-end">
                                                            <span className={`text-xl font-black ${getScoreColor(Number(course.avgScore)).text}`}>
                                                                {course.avgScore}%
                                                            </span>
                                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Avg Score</span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col items-end">
                                                            <span className="text-xs font-black text-rose-500 bg-rose-50 border border-rose-100 px-2 py-0.5 rounded-md uppercase tracking-wider animate-pulse">
                                                                Locked
                                                            </span>
                                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1">Not Started</span>
                                                        </div>
                                                    )}
                                                </div>

                                                <h3 className="text-lg font-black text-slate-800 mb-4 line-clamp-1 group-hover:text-[#264b82] transition-colors">
                                                    {course.name}
                                                </h3>

                                                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-slate-50">
                                                    <div className="bg-slate-50/50 rounded-xl p-3">
                                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Attempts</p>
                                                        <p className="text-sm font-black text-slate-700">{course.results.length}</p>
                                                    </div>
                                                    <div className="bg-emerald-50/50 rounded-xl p-3">
                                                        <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mb-0.5">Passed</p>
                                                        <p className="text-sm font-black text-emerald-700">{course.passedCount}</p>
                                                    </div>
                                                </div>

                                                <button className="mt-5 w-full py-3 bg-slate-50 group-hover:bg-[#264b82] group-hover:text-white text-slate-400 text-[11px] font-black uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2">
                                                    {isCourseStarted ? "View Detailed Results" : "Unlock Course"} <ChevronRight size={14} />
                                                </button>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="col-span-full">
                                        <EmptyState hasSearch={!!searchQuery} />
                                    </div>
                                )}
                            </div>
                        ) : (
                            /* Detailed Results or Locked View for Selected Course */
                            isSelectedCourseStarted ? (
                                <>
                                    {filteredRecords.length > 0 ? (
                                        <div className="space-y-3">
                                            <div className="flex justify-between items-center px-1 mb-4">
                                                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                                                    {totalQuizCount} Assessment{totalQuizCount !== 1 ? "s" : ""} Found
                                                </p>
                                                <button
                                                    onClick={() => setSelectedCourse(null)}
                                                    className="text-xs font-bold text-[#264b82] hover:underline"
                                                >
                                                    Back to All Courses
                                                </button>
                                            </div>

                                            {filteredRecords.map((ele, index) => {
                                                const totalQ = ele.rightAns + ele.wrongAns;
                                                const pct = totalQ > 0 ? Math.round((ele.rightAns / totalQ) * 100) : 0;
                                                const colors = getScoreColor(pct);

                                                return (
                                                    <div
                                                        key={index}
                                                        className="bg-white rounded-2xl border border-slate-150 shadow-sm hover:shadow-md hover:border-slate-200 transition-all group relative overflow-hidden"
                                                    >
                                                        <div className={`absolute left-0 top-0 w-1 h-full ${colors.bg}`}></div>

                                                        <div className="pl-6 pr-6 py-5 flex flex-col lg:flex-row items-start lg:items-center gap-5">
                                                            <div className="flex items-center gap-4 min-w-[180px]">
                                                                <div className="w-11 h-11 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-500 font-black text-base flex-shrink-0">
                                                                    {ele.StudentName?.charAt(0) || "S"}
                                                                </div>
                                                                <div>
                                                                    <p className="font-bold text-slate-800 text-sm leading-tight">{ele.StudentName || "Student"}</p>
                                                                    <div className="flex items-center gap-1 text-slate-400 text-[11px] font-semibold mt-0.5">
                                                                        <Calendar size={11} />
                                                                        {ele.createdOn ? moment(ele.createdOn).format("MMM D, YYYY") : "—"}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <Target size={14} className="text-[#264b82] flex-shrink-0" />
                                                                    <h4 className="font-bold text-slate-800 text-sm group-hover:text-[#264b82] transition-colors truncate">
                                                                        {ele.name || "Untitled Quiz"}
                                                                    </h4>
                                                                </div>
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <span className="flex items-center gap-1 bg-slate-50 border border-slate-100 text-slate-500 text-[11px] font-bold px-2.5 py-1 rounded-lg">
                                                                        <Hash size={10} />
                                                                        Attempt #{(pageNum - 1) * 8 + index + 1}
                                                                    </span>
                                                                    <span className="flex items-center gap-1 bg-emerald-50 border border-emerald-100 text-emerald-600 text-[11px] font-bold px-2.5 py-1 rounded-lg">
                                                                        <ArrowUpRight size={10} />
                                                                        {ele.rightAns} Correct
                                                                    </span>
                                                                    <span className="flex items-center gap-1 bg-rose-50 border border-rose-100 text-rose-500 text-[11px] font-bold px-2.5 py-1 rounded-lg">
                                                                        <XCircle size={10} />
                                                                        {ele.wrongAns} Wrong
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <div className="flex items-center gap-6 w-full lg:w-auto border-t lg:border-t-0 lg:border-l border-slate-100 pt-4 lg:pt-0 lg:pl-6">
                                                                <div className="flex flex-col items-center gap-1 min-w-[64px]">
                                                                    <span className={`text-3xl font-black ${colors.text}`}>{pct}</span>
                                                                    <span className="text-slate-400 text-[11px] font-bold">percent</span>
                                                                    <div className="w-full h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
                                                                        <div
                                                                            className={`h-full rounded-full ${colors.bg} transition-all duration-700`}
                                                                            style={{ width: `${pct}%` }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div>
                                                                    <div className="flex flex-col gap-2">
                                                                        {pct >= 70 ? (
                                                                            <span className={`flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider px-3 py-1.5 rounded-xl border ${colors.badge}`}>
                                                                                <CheckCircle2 size={12} /> Passed
                                                                            </span>
                                                                        ) : (
                                                                            <span className={`flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider px-3 py-1.5 rounded-xl border ${colors.badge}`}>
                                                                                <XCircle size={12} /> Failed
                                                                            </span>
                                                                        )}
                                                                        <button
                                                                            onClick={() => handleOpenReview(ele.id)}
                                                                            className="w-full text-center px-3 py-1.5 bg-slate-50 hover:bg-[#264b82] hover:text-white text-[#264b82] border border-slate-200 hover:border-[#264b82] rounded-xl text-[11px] font-black transition-all shadow-sm active:scale-95 whitespace-nowrap"
                                                                        >
                                                                            Review Answers
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {totalQuizPage > 1 && (
                                                <div className="mt-8 flex row">
                                                    <div className="bg-white col p-2 rounded-2xl shadow-sm border border-slate-100">
                                                        <Pagination
                                                            currentPage={pageNum}
                                                            totalCount={totalQuizPage * 8}
                                                            pageSize={8}
                                                            onPageChange={(page) => setPageNum(page)}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            <div className="flex justify-between items-center px-1 mb-4">
                                                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                                                    0 Assessments Found
                                                </p>
                                                <button
                                                    onClick={() => setSelectedCourse(null)}
                                                    className="text-xs font-bold text-[#264b82] hover:underline"
                                                >
                                                    Back to All Courses
                                                </button>
                                            </div>
                                            <EmptyState hasSearch={!!searchQuery} />
                                        </div>
                                    )}
                                </>
                            ) : (
                                /* Lock Screen / Not Started View for Selected Course */
                                <div className="flex flex-col items-center justify-center min-h-[60vh] bg-white rounded-[2rem] border border-slate-200 p-8 md:p-16 text-center max-w-2xl mx-auto shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
                                    <div className="bg-amber-50 w-24 h-24 rounded-3xl flex items-center justify-center mb-6 shadow-sm border border-amber-100">
                                        <PlayCircle size={48} className="text-amber-500" />
                                    </div>
                                    <h3 className="text-2xl font-black text-slate-800 tracking-tight mb-4 max-w-lg leading-snug">
                                        This course has not been started yet
                                    </h3>
                                    <p className="text-slate-500 text-base font-semibold max-w-md mb-8 leading-relaxed">
                                        To view performance analytics for "{selectedCourse}", you must first start learning this course.
                                    </p>
                                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                        <button
                                            onClick={() => navigate("/dashboard/course/list")}
                                            className="group flex items-center gap-2 bg-[#264b82] text-white hover:bg-[#1e3a6e] px-8 py-4 rounded-2xl font-black text-base uppercase tracking-wider transition-all shadow-lg shadow-blue-900/15 active:scale-95 border border-[#264b82]"
                                        >
                                            Start Learning <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                                        </button>
                                        <button
                                            onClick={() => setSelectedCourse(null)}
                                            className="group flex items-center gap-2 bg-slate-100 text-slate-700 hover:bg-slate-200 px-8 py-4 rounded-2xl font-black text-base uppercase tracking-wider transition-all active:scale-95 border border-transparent"
                                        >
                                            Back to Courses
                                        </button>
                                    </div>
                                </div>
                            )
                        )}
                    </>
                )}
            </div>

            {/* Exam Review Side Drawer */}
            <Modal isOpen={isReviewModalOpen} onClose={handleCloseReview} width="650px">
                {loadingReview ? (
                    <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-slate-800"></div>
                        <p className="text-slate-500 font-bold text-sm mt-4">Loading exam details...</p>
                    </div>
                ) : reviewError ? (
                    <div className="text-center py-10 px-4">
                        <XCircle className="text-rose-500 w-12 h-12 mx-auto mb-4" />
                        <h3 className="text-lg font-bold text-slate-800">Review Error</h3>
                        <p className="text-slate-500 text-sm mt-2">{reviewError}</p>
                        <button
                            onClick={() => handleOpenReview(selectedTestId)}
                            className="mt-4 px-4 py-2 bg-slate-800 text-white rounded-xl text-xs font-bold"
                        >
                            Retry
                        </button>
                    </div>
                ) : reviewData ? (
                    <div className="space-y-6 pt-4 text-left">
                        {/* Modal Header Card */}
                        <div className="bg-slate-50 border border-slate-200/60 rounded-2xl p-5 relative overflow-hidden shadow-sm">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-slate-100 rounded-full -mr-8 -mt-8 opacity-40 pointer-events-none"></div>
                            
                            <div className="flex justify-between items-start">
                                <div>
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-200 text-slate-800">
                                        Exam Review
                                    </span>
                                    <h3 className="text-xl font-black text-slate-800 mt-2 leading-tight">
                                        {reviewData.name || "Untitled Quiz"}
                                    </h3>
                                    <p className="text-slate-500 text-xs font-semibold mt-1">
                                        Course: {reviewData.CourseName || "General Quiz"}
                                    </p>
                                </div>
                                
                                <div className="text-right">
                                    <div className="inline-flex flex-col items-center justify-center bg-white border border-slate-200/80 rounded-2xl p-3 shadow-sm min-w-[70px]">
                                        <span className="text-xl font-black text-slate-800">
                                            {Math.round((reviewData.rightAns / (reviewData.rightAns + reviewData.wrongAns || 1)) * 100)}%
                                        </span>
                                        <span className="text-[9px] text-slate-400 font-extrabold uppercase mt-0.5">Score</span>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mt-5 border-t border-slate-200/60 pt-4">
                                <div>
                                    <p className="text-[10px] text-slate-400 font-bold tracking-wider uppercase">Student Name</p>
                                    <p className="text-sm font-bold text-slate-700 mt-0.5">{reviewData.StudentName}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] text-slate-400 font-bold tracking-wider uppercase">Attempted On</p>
                                    <p className="text-sm font-bold text-slate-700 mt-0.5">
                                        {reviewData.createdOn ? moment(reviewData.createdOn).format("MMM DD, YYYY hh:mm A") : "—"}
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-4 mt-4 bg-white/60 p-2.5 rounded-xl border border-slate-200/40 text-xs">
                                <div className="flex items-center gap-1.5 font-semibold text-emerald-700">
                                    <CheckCircle2 size={14} className="text-emerald-600" />
                                    <span>{reviewData.rightAns} Correct</span>
                                </div>
                                <div className="flex items-center gap-1.5 font-semibold text-rose-700">
                                    <XCircle size={14} className="text-rose-600" />
                                    <span>{reviewData.wrongAns} Incorrect</span>
                                </div>
                                <div className="flex items-center gap-1.5 font-semibold text-slate-600 ml-auto">
                                    <Target size={14} className="text-slate-500" />
                                    <span>Total {reviewData.rightAns + reviewData.wrongAns} Qs</span>
                                </div>
                            </div>
                        </div>

                        {/* Questions list */}
                        <div className="space-y-5">
                            <h4 className="text-sm font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
                                <BookOpen size={16} className="text-slate-500" />
                                Questions Breakdown
                            </h4>

                            {reviewData.questions && reviewData.questions.length > 0 ? (
                                reviewData.questions.map((q, idx) => {
                                    const isAnswered = q.options && q.options.some(opt => opt.isSelected);
                                    const isQuestionCorrect = q.options && q.options.every(opt => opt.isRight === opt.isSelected);

                                    return (
                                        <div
                                            key={q.id || idx}
                                            className="border border-slate-200 rounded-2xl p-5 bg-white transition-all shadow-sm hover:border-slate-300"
                                        >
                                            {/* Question Header */}
                                            <div className="flex items-start justify-between gap-3 mb-4">
                                                <div className="flex gap-2">
                                                    <span className="text-xs font-black text-slate-400 mt-0.5">
                                                        Q{idx + 1}.
                                                    </span>
                                                    <p className="text-sm font-extrabold text-slate-800 leading-relaxed">
                                                        {q.question}
                                                    </p>
                                                </div>
                                                <div>
                                                    {isQuestionCorrect ? (
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-50 border border-emerald-250 text-emerald-700">
                                                            <CheckCircle2 size={10} className="stroke-[3]" /> Correct
                                                        </span>
                                                    ) : isAnswered ? (
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-rose-50 border border-rose-250 text-rose-700">
                                                            <XCircle size={10} className="stroke-[3]" /> Incorrect
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-slate-100 border border-slate-200 text-slate-500">
                                                            Skipped
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Options List */}
                                            <div className="space-y-2.5">
                                                {q.options && q.options.map((opt, oIdx) => {
                                                    const isSelected = opt.isSelected;
                                                    const isRight = opt.isRight;

                                                    let optionStyle = "border-slate-200 bg-white text-slate-700 hover:bg-slate-50/50";
                                                    let badgeText = "";
                                                    let badgeStyle = "";

                                                    if (isSelected && isRight) {
                                                        optionStyle = "border-emerald-500 bg-emerald-50/40 text-emerald-950";
                                                        badgeText = "Correct Choice";
                                                        badgeStyle = "bg-emerald-500 text-white";
                                                    } else if (isSelected && !isRight) {
                                                        optionStyle = "border-rose-300 bg-rose-50/40 text-rose-950";
                                                        badgeText = "Incorrect Choice";
                                                        badgeStyle = "bg-rose-500 text-white";
                                                    } else if (!isSelected && isRight) {
                                                        optionStyle = "border-emerald-500 bg-emerald-50/20 text-emerald-900";
                                                        badgeText = "Correct Answer";
                                                        badgeStyle = "bg-emerald-100 text-emerald-850 border border-emerald-200";
                                                    }

                                                    const label = String.fromCharCode(65 + oIdx);

                                                    return (
                                                        <div
                                                            key={opt.id || oIdx}
                                                            className={`flex items-center justify-between border rounded-xl p-3 text-xs font-semibold transition-all relative ${optionStyle}`}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-black border ${
                                                                    isSelected && isRight
                                                                        ? "bg-emerald-500 border-emerald-500 text-white"
                                                                        : isSelected && !isRight
                                                                        ? "bg-rose-500 border-rose-500 text-white"
                                                                        : !isSelected && isRight
                                                                        ? "bg-emerald-100 border-emerald-300 text-emerald-700"
                                                                        : "bg-slate-100 border-slate-200 text-slate-500"
                                                                }`}>
                                                                    {label}
                                                                </span>
                                                                <span className="text-slate-700 font-medium">{opt.answer}</span>
                                                            </div>
                                                            
                                                            {badgeText && (
                                                                <span className={`px-2 py-0.5 rounded-md text-[8px] font-black uppercase tracking-wider ${badgeStyle}`}>
                                                                    {badgeText}
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <p className="text-slate-400 text-sm font-semibold text-center py-6">No questions found for this quiz.</p>
                            )}
                        </div>
                    </div>
                ) : null}
            </Modal>

            {/* Backdrop Blur Overlay */}
            {isReviewModalOpen && (
                <div 
                    className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300 z-[999]" 
                    onClick={handleCloseReview}
                />
            )}
        </div>
    );
};

const Sk = ({ className }) => (
    <div className={`bg-slate-200 animate-pulse rounded-xl ${className}`} />
);

const ResultsSkeleton = ({ isCourseList }) => (
    <>
        {isCourseList ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-white rounded-[2rem] border border-slate-100 p-6 space-y-4">
                        <div className="flex justify-between items-start">
                            <Sk className="w-12 h-12 rounded-2xl" />
                            <div className="space-y-1 items-end flex flex-col">
                                <Sk className="h-6 w-12 rounded-lg" />
                                <Sk className="h-2 w-10 rounded-lg" />
                            </div>
                        </div>
                        <Sk className="h-6 w-3/4 rounded-lg" />
                        <div className="grid grid-cols-2 gap-3 pt-4">
                            <Sk className="h-12 rounded-xl" />
                            <Sk className="h-12 rounded-xl" />
                        </div>
                        <Sk className="h-12 w-full rounded-xl" />
                    </div>
                ))}
            </div>
        ) : (
            <div className="space-y-3">
                <Sk className="h-3 w-40 rounded-lg mb-4" />
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="bg-white rounded-2xl border border-slate-100 pl-6 pr-6 py-5 flex flex-col lg:flex-row items-start lg:items-center gap-5 relative overflow-hidden">
                        <div className="absolute left-0 top-0 w-1 h-full bg-slate-200" />
                        <div className="flex items-center gap-4 min-w-[180px]">
                            <Sk className="w-11 h-11 rounded-2xl flex-shrink-0" />
                            <div className="space-y-2">
                                <Sk className="h-4 w-28 rounded-lg" />
                                <Sk className="h-3 w-20 rounded-lg" />
                            </div>
                        </div>
                        <div className="flex-1 space-y-3">
                            <div className="flex items-center gap-2">
                                <Sk className="w-4 h-4 rounded flex-shrink-0" />
                                <Sk className="h-4 w-48 rounded-lg" />
                            </div>
                            <div className="flex gap-2">
                                <Sk className="h-7 w-20 rounded-lg" />
                                <Sk className="h-7 w-24 rounded-lg" />
                                <Sk className="h-7 w-20 rounded-lg" />
                            </div>
                        </div>
                        <div className="flex items-center gap-6 border-t lg:border-t-0 lg:border-l border-slate-100 pt-4 lg:pt-0 lg:pl-6 w-full lg:w-auto">
                            <div className="space-y-2">
                                <Sk className="h-8 w-12 rounded-lg mx-auto" />
                                <Sk className="h-3 w-12 rounded-lg" />
                                <Sk className="h-1.5 w-16 rounded-full" />
                            </div>
                            <Sk className="h-8 w-20 rounded-xl" />
                        </div>
                    </div>
                ))}
            </div>
        )}
    </>
);

const StatCard = ({ icon, iconBg, label, value, sub }) => (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 flex items-center gap-5 group hover:shadow-md transition-all">
        <div className={`p-3 rounded-2xl transition-all ${iconBg}`}>
            {icon}
        </div>
        <div>
            <p className="text-slate-400 text-[11px] font-black uppercase tracking-widest mb-0.5">{label}</p>
            <p className="text-2xl font-black text-slate-800 leading-none">{value}</p>
            <p className="text-slate-400 text-xs font-medium mt-0.5">{sub}</p>
        </div>
    </div>
);

const EmptyState = ({ hasSearch }) => (
    <div className="bg-white rounded-3xl border border-slate-100 p-16 flex flex-col items-center justify-center text-center">
        <div className="bg-slate-50 w-20 h-20 rounded-3xl flex items-center justify-center mb-5">
            {hasSearch ? <Search size={36} className="text-slate-300" /> : <Inbox size={36} className="text-slate-300" />}
        </div>
        <h4 className="text-xl font-bold text-slate-700 mb-1.5">
            {hasSearch ? "No Results Found" : "No Records Yet"}
        </h4>
        <p className="text-slate-400 text-sm font-medium max-w-xs leading-relaxed">
            {hasSearch
                ? "Try adjusting your search to find what you're looking for."
                : "Complete your first assessment to see your performance metrics here."
            }
        </p>
    </div>
);

export default QuizResult;
